// hooks/useTypeaheadSearch.ts
import { useEffect, useMemo, useState } from 'react';
import Fuse from 'fuse.js';
import type { Product } from '../types/product';

type Options = {
  debounceDelay?: number;
  minQueryLength?: number;
};

export default function useTypeaheadSearch(
  items: Product[],
  query: string,
  { debounceDelay = 300, minQueryLength = 2 }: Options = {}
) {
  const [results, setResults] = useState<Product[]>([]);
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  // Debounce logic
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedQuery(query);
    }, debounceDelay);

    return () => clearTimeout(id);
  }, [query, debounceDelay]);

  // Prepare Fuse once for the current dataset
  const fuse = useMemo(() => {
    if (!items || items.length === 0) return null;

    return new Fuse(items, {
      keys: [
        { name: 'name', weight: 0.6 },
        { name: 'description', weight: 0.2 },
        { name: 'longDescription', weight: 0.2 },
      ],
      includeScore: true,
      ignoreLocation: true,
      threshold: 0.28,
    });
  }, [items]);

  // Perform search whenever debounced query changes
  useEffect(() => {
    if (!fuse) {
      setResults([]);
      return;
    }

    const q = debouncedQuery.trim();

    if (q.length < minQueryLength) {
      setResults([]); // don’t show results until query is long enough
      return;
    }

    const matches = fuse.search(q).map((r) => r.item);
    setResults(matches);
  }, [debouncedQuery, fuse, minQueryLength]);

  return {
    results,
    count: results.length,
    debouncedQuery,
    isDebouncing: debouncedQuery !== query,
  };
}
